import jwtDecode from 'jwt-decode';
import {
    login,
    setCatalogName,
    setUsuarioConfig,
    setUsuarioBySystem,
    setRotas,
    setIdUsuario,
    setNomeUsuario,
    setEmailUsuario,
    getCatalogName
} from './auth';
import {
    getCachedCatalogSession,
    setCachedCatalogSession,
    removeCachedCatalogSession,
    setCachedCatalogListData
} from './catalogTokenCache';
import * as usuarioService from './usuarioService';
import * as transacaoSistemaService from './transacaoSistemaService';
import * as cargoService from './cargoService';
import * as perfilAcessoService from './perfilAcessoService';

export function applyTokenToSession(token, profile = null) {
    login(token);
    const decoded = jwtDecode(token);
    const user = decoded && decoded.user ? decoded.user : {};
    setCatalogName(user.catalogName ? user.catalogName : '');
    setUsuarioConfig(user.config ? user.config : {});

    const sessionProfile = {
        id: profile?.id ?? user.id ?? null,
        nome: profile?.nome ?? user.nome ?? null,
        email: profile?.email ?? user.email ?? null
    };

    const catalogName = user.catalogName || getCatalogName();
    if (catalogName && token) {
        setCachedCatalogSession(catalogName, {
            token,
            ...sessionProfile
        });
    }

    return decoded;
}

export function applyUserProfile({ id, nome, email }) {
    if (id != null) {
        setIdUsuario(id);
    }
    if (nome != null) {
        setNomeUsuario(nome);
    }
    if (email != null) {
        setEmailUsuario(email);
    }
}

export function clearAllowedRoutes() {
    localStorage.setItem('rotasPermitidas', '[]');
}

function addRotaPermitida(rota) {
    const rotas = JSON.parse(localStorage.getItem('rotasPermitidas') || '[]');
    if (!rotas.includes(rota)) {
        rotas.push(rota);
    }
    localStorage.setItem('rotasPermitidas', JSON.stringify(rotas));
}

function montarMenuFilhos(transacoesDisponiveis, transacoesComAcesso, codigoIdentificacaoPai) {
    const actions = [];
    let transacoesComAcessoNoMenu = 0;

    transacoesDisponiveis.forEach((i) => {
        if (i.codigoIdentificacaoPai && i.codigoIdentificacaoPai === codigoIdentificacaoPai) {
            if (i.url) {
                const temAcesso = transacoesComAcesso.filter((t) => t.id === i.id).length > 0;
                if (temAcesso) {
                    transacoesComAcessoNoMenu++;
                    addRotaPermitida(i.url);
                }

                actions.push({
                    id: i.code,
                    title: i.nome,
                    type: 'item',
                    url: temAcesso ? i.url : '/',
                    icon: i.icon,
                    disabled: !temAcesso,
                    breadcrumbs: false
                });
            } else {
                const menusFilhos = montarMenuFilhos(
                    transacoesDisponiveis,
                    transacoesComAcesso,
                    i.codigoIdentificacao
                );

                if (menusFilhos.transacoesComAcessoNoMenu !== 0) {
                    transacoesComAcessoNoMenu++;
                }

                actions.push({
                    id: i.code,
                    title: i.nome,
                    type: 'collapse',
                    icon: i.icon,
                    children: menusFilhos.actions,
                    disabled: menusFilhos.transacoesComAcessoNoMenu === 0,
                    breadcrumbs: false
                });
            }
        }
    });

    return {
        actions,
        transacoesComAcessoNoMenu
    };
}

function montarMenuPais(transacoesDisponiveis, transacoesComAcesso) {
    const rotasEncontradas = [];
    transacoesDisponiveis.forEach((i) => {
        if (!i.codigoIdentificacaoPai || i.codigoIdentificacaoPai === '') {
            const menusFilhos = montarMenuFilhos(
                transacoesDisponiveis,
                transacoesComAcesso,
                i.codigoIdentificacao
            );

            rotasEncontradas.push({
                id: i.code,
                title: i.nome,
                type: 'collapse',
                icon: i.icon,
                children: menusFilhos.actions,
                disabled: menusFilhos.transacoesComAcessoNoMenu === 0,
                breadcrumbs: false
            });
        }
    });
    return rotasEncontradas;
}

async function montarMenu(transacoesDisponiveis, perfilAcessoUsuario) {
    const perfilAcesso = await perfilAcessoService.get(perfilAcessoUsuario.id);
    const transacoesComAcesso = perfilAcesso.data.transacoesSistema;
    const rotas = montarMenuPais(transacoesDisponiveis, transacoesComAcesso);
    setRotas(JSON.stringify(rotas));
}

async function montarMenuParaUsuario(usuario) {
    const transacoes = await transacaoSistemaService.list('');
    const transacoesDisponiveis = transacoes.data;
    transacoesDisponiveis.sort((a, b) => (a.sequenciaExibicao < b.sequenciaExibicao ? -1 : 1));

    const u = usuario.data || {};
    setUsuarioBySystem(u.bySystem === true || u.bySystem === 'true');

    let perfilAcesso = usuario.data.perfilAcesso;
    if (perfilAcesso && perfilAcesso.id !== '') {
        await montarMenu(transacoesDisponiveis, perfilAcesso);
        return;
    }

    const cargo = await cargoService.get(usuario.data.cargo.id);
    await montarMenu(transacoesDisponiveis, cargo.data.perfilAcesso);
}

export async function rebuildMenuAccess(userId) {
    clearAllowedRoutes();
    const usuario = await usuarioService.get(userId);
    await montarMenuParaUsuario(usuario);
}

/** Carrega e persiste a listagem de catálogos do usuário (uma vez por login). */
export async function cacheUserCatalogList() {
    const response = await usuarioService.listCatalogs();
    if (response.data) {
        setCachedCatalogListData(response.data);
    }
    return response;
}

export async function switchCatalogSession(catalogName) {
    const cached = getCachedCatalogSession(catalogName);

    if (cached) {
        applyTokenToSession(cached.token, {
            id: cached.id,
            nome: cached.nome,
            email: cached.email
        });
        applyUserProfile({
            id: cached.id,
            nome: cached.nome,
            email: cached.email
        });

        try {
            await rebuildMenuAccess(cached.id);
            return { status: 200, data: cached, fromCache: true };
        } catch (error) {
            removeCachedCatalogSession(catalogName);
            if (!error?.response || error.response.status !== 401) {
                throw error;
            }
        }
    }

    const response = await usuarioService.switchCatalog(catalogName);
    if (response.status !== 200 && response.status !== 201) {
        return response;
    }

    const { token, id, nome, email } = response.data;
    applyTokenToSession(token, { id, nome, email });
    applyUserProfile({ id, nome, email });
    setCachedCatalogSession(catalogName, { token, id, nome, email });
    await rebuildMenuAccess(id);

    return response;
}
